<script setup>
// SiteUp: aba "Produtos do Cliente" no painel da conversa (só na conta habilitada —
// o guard de conta fica no pai). 3 seções colapsáveis: Cursos em andamento,
// Livros adquiridos, Eventos adquiridos. Dados resolvidos pelo backend via
// GET contacts/:id/produtos (inicial cacheada + polling visível com refresh). Datas
// pt-BR; badge de validade nos cursos. Tolerante a falha da API.
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import ContactProdutosAPI from 'dashboard/api/contactProdutos';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], default: null },
});

const loading = ref(false);
const cursos = ref([]);
const livros = ref([]);
const eventos = ref([]);
const open = ref({ cursos: true, livros: false, eventos: false });
// A fonte externa não publica eventos no ActionCable desta branch. Enquanto o
// painel e a aba estão visíveis, o polling serial limita a latência a 15s + HTTP.
const PRODUCTS_POLL_INTERVAL_MS = 15000;
const REALTIME_DEBOUNCE_MS = 500;
let pollTimer = null;
let realtimeRefreshTimer = null;
let inFlightRequest = null;
let abortController = null;
let pendingFetch = null;
let isMounted = false;

const isPanelVisible = () => document.visibilityState === 'visible';

const clearPollTimer = () => {
  if (!pollTimer) return;
  clearTimeout(pollTimer);
  pollTimer = null;
};

const clearRealtimeRefreshTimer = () => {
  if (!realtimeRefreshTimer) return;
  clearTimeout(realtimeRefreshTimer);
  realtimeRefreshTimer = null;
};

const fetchData = ({ refresh = false } = {}) => {
  const contactId = props.contactId;
  if (!contactId || !isMounted) return Promise.resolve();

  if (inFlightRequest) {
    pendingFetch = { refresh: Boolean(pendingFetch?.refresh || refresh) };
    return inFlightRequest;
  }

  const showLoading = !refresh;
  if (showLoading) loading.value = true;

  const controller = new AbortController();
  abortController = controller;
  inFlightRequest = ContactProdutosAPI.get(contactId, {
    refresh,
    signal: controller.signal,
  })
    .then(({ data }) => {
      if (!isMounted || String(props.contactId) !== String(contactId)) return;
      cursos.value = Array.isArray(data?.cursos) ? data.cursos : [];
      livros.value = Array.isArray(data?.livros) ? data.livros : [];
      eventos.value = Array.isArray(data?.eventos) ? data.eventos : [];
    })
    .catch(() => {
      if (!isMounted || controller.signal.aborted || refresh) return;
      cursos.value = [];
      livros.value = [];
      eventos.value = [];
    })
    .finally(() => {
      if (abortController === controller) abortController = null;
      if (showLoading && String(props.contactId) === String(contactId)) {
        loading.value = false;
      }
      inFlightRequest = null;

      if (pendingFetch && isMounted && isPanelVisible()) {
        const nextFetch = pendingFetch;
        pendingFetch = null;
        fetchData(nextFetch);
      }
    });

  return inFlightRequest;
};

const schedulePoll = () => {
  clearPollTimer();
  if (!isMounted || !isPanelVisible()) return;

  pollTimer = setTimeout(async () => {
    pollTimer = null;
    await fetchData({ refresh: true });
    schedulePoll();
  }, PRODUCTS_POLL_INTERVAL_MS);
};

const startPolling = async ({ refreshImmediately = false } = {}) => {
  clearPollTimer();
  if (!isMounted || !isPanelVisible()) return;

  await fetchData({ refresh: refreshImmediately });
  schedulePoll();
};

const isCurrentConversation = conversationId =>
  props.conversationId &&
  conversationId &&
  String(props.conversationId) === String(conversationId);

const refreshFromRealtime = () => {
  if (!isMounted || !isPanelVisible()) return;
  clearRealtimeRefreshTimer();

  realtimeRefreshTimer = setTimeout(async () => {
    realtimeRefreshTimer = null;
    await fetchData({ refresh: true });
    schedulePoll();
  }, REALTIME_DEBOUNCE_MS);
};

const handleKanbanItemRealtime = item => {
  if (isCurrentConversation(item?.conversation_display_id)) {
    refreshFromRealtime();
  }
};

const handleConversationRealtime = conversationId => {
  if (isCurrentConversation(conversationId)) {
    refreshFromRealtime();
  }
};

const handleVisibilityChange = () => {
  clearPollTimer();
  clearRealtimeRefreshTimer();
  pendingFetch = null;

  if (!isPanelVisible()) {
    abortController?.abort();
    return;
  }

  startPolling({ refreshImmediately: true });
};

const handleContactChange = () => {
  clearPollTimer();
  clearRealtimeRefreshTimer();
  pendingFetch = null;
  loading.value = Boolean(props.contactId);
  cursos.value = [];
  livros.value = [];
  eventos.value = [];
  abortController?.abort();
  startPolling();
};

onMounted(() => {
  isMounted = true;
  emitter.on('kanbanItemCreated', handleKanbanItemRealtime);
  emitter.on('kanbanItemUpdated', handleKanbanItemRealtime);
  emitter.on('conversation.updated', handleConversationRealtime);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  startPolling();
});

onBeforeUnmount(() => {
  isMounted = false;
  clearPollTimer();
  clearRealtimeRefreshTimer();
  pendingFetch = null;
  abortController?.abort();
  emitter.off('kanbanItemCreated', handleKanbanItemRealtime);
  emitter.off('kanbanItemUpdated', handleKanbanItemRealtime);
  emitter.off('conversation.updated', handleConversationRealtime);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

watch(() => props.contactId, handleContactChange);

// ISO (YYYY-MM-DD...) → DD/MM/YYYY sem cair em fuso horário.
const fmtDate = iso => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('T')[0].split('-');
  return y && m && d ? `${d}/${m}/${y}` : '—';
};

const toggle = key => {
  open.value[key] = !open.value[key];
};

// Badge de validade do curso: Vitalício (cinza) ou "faltam N dias" (vermelho <30,
// laranja <90, verde ≥90; vermelho se já expirou).
const validade = curso => {
  if (curso.vitalicio) {
    return { label: 'Vitalício', cls: 'bg-n-slate-3 text-n-slate-11' };
  }
  const dias = curso.dias_a_expirar;
  if (dias === null || dias === undefined) return null;
  if (dias <= 0) {
    return { label: 'Expirado', cls: 'bg-n-ruby-9/15 text-n-ruby-11' };
  }
  const cls =
    dias < 30
      ? 'bg-n-ruby-9/15 text-n-ruby-11'
      : dias < 90
        ? 'bg-n-amber-9/15 text-n-amber-11'
        : 'bg-n-teal-9/15 text-n-teal-11';
  return { label: `faltam ${dias} dias`, cls };
};

const sections = computed(() => [
  { key: 'cursos', icon: 'i-ph-graduation-cap', label: 'Cursos em andamento', count: cursos.value.length },
  { key: 'livros', icon: 'i-ph-book-open', label: 'Livros adquiridos', count: livros.value.length },
  { key: 'eventos', icon: 'i-ph-ticket', label: 'Eventos adquiridos', count: eventos.value.length },
]);

const emptyText = {
  cursos: 'Nenhum curso ativo.',
  livros: 'Nenhum livro.',
  eventos: 'Nenhum evento.',
};
</script>

<template>
  <div class="mx-2 mb-2 border rounded-xl border-n-weak bg-n-solid-1">
    <div
      class="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-n-slate-12"
    >
      <span class="i-ph-shopping-bag size-4 text-n-slate-11" />
      Produtos do Cliente
    </div>

    <!-- Loading: skeleton -->
    <div v-if="loading" class="flex flex-col gap-2 px-3 pb-3">
      <div
        v-for="n in 3"
        :key="n"
        class="h-8 rounded-md bg-n-alpha-2 animate-pulse"
      />
    </div>

    <template v-else>
      <div
        v-for="section in sections"
        :key="section.key"
        class="border-t border-n-weak"
      >
        <button
          type="button"
          class="flex items-center w-full gap-2 px-3 py-2 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-1"
          @click="toggle(section.key)"
        >
          <span :class="section.icon" class="size-3.5 shrink-0 text-n-slate-11" />
          <span class="flex-1 text-left">{{ section.label }}</span>
          <span class="text-n-slate-10">({{ section.count }})</span>
          <span
            :class="
              open[section.key] ? 'i-ph-caret-up' : 'i-ph-caret-down'
            "
            class="size-3.5 shrink-0 text-n-slate-10"
          />
        </button>

        <div v-if="open[section.key]" class="px-3 pb-2.5">
          <!-- Cursos -->
          <template v-if="section.key === 'cursos'">
            <p v-if="!cursos.length" class="py-1 text-xs text-n-slate-10">
              {{ emptyText.cursos }}
            </p>
            <div
              v-for="(curso, idx) in cursos"
              :key="idx"
              class="flex flex-col gap-0.5 py-1.5 border-b border-n-weak last:border-0"
            >
              <div class="flex items-start justify-between gap-2">
                <span class="text-xs font-medium text-n-slate-12">
                  {{ curso.curso }}
                </span>
                <span
                  v-if="validade(curso)"
                  class="px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap shrink-0"
                  :class="validade(curso).cls"
                >
                  {{ validade(curso).label }}
                </span>
              </div>
              <div class="flex items-center gap-2 text-[11px] text-n-slate-10">
                <span>{{ curso.duracao }}</span>
                <span>·</span>
                <span>Compra: {{ fmtDate(curso.data_compra) }}</span>
                <template v-if="curso.expiracao">
                  <span>·</span>
                  <span>Expira: {{ fmtDate(curso.expiracao) }}</span>
                </template>
              </div>
            </div>
          </template>

          <!-- Livros / Eventos: lista simples -->
          <template v-else>
            <p
              v-if="!(section.key === 'livros' ? livros : eventos).length"
              class="py-1 text-xs text-n-slate-10"
            >
              {{ emptyText[section.key] }}
            </p>
            <div
              v-for="(row, idx) in section.key === 'livros' ? livros : eventos"
              :key="idx"
              class="flex items-center justify-between gap-2 py-1.5 border-b border-n-weak last:border-0"
            >
              <span class="text-xs text-n-slate-12">{{ row.produto }}</span>
              <span class="text-[11px] text-n-slate-10 whitespace-nowrap shrink-0">
                {{ fmtDate(row.data_compra) }}
              </span>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
